import React, { FC, useEffect, useState } from "react";
import { DEF_PG_SIZE_SUB } from '../../constants/maximum-page-size';
import { useLocale } from "../../contexts/localeContext";
import { TableContextProvider } from "../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { AccessGroupAction } from "../../models/AccessGroupAction";
import columns from '../../models/ColumnDefinitions/AccessGroupActionColumns';
import accessGroupDeviceActionColumns from "../../models/ColumnDefinitions/AccessGroupDeviceActionColumn";
import { ListData } from "../../models/ListData";
import { ListQuery } from "../../models/ListQuery";
import { Lookup } from "../../models/Lookup";
import LookupTypes from "../../models/LookupTypes";
import searchColumns from '../../models/search/AccessGroupActionSearch';
import accessGroupDeviceActionSearch from "../../models/search/AccessGroupDeviceActionSearch";
import SiteSearch from "../../models/search/SiteSearch";
import { TabSummary } from "../../models/TabSummary";
import { deleteAccessGroupAction, getAccessGroupActions, getDeviceActionsForAccessGroups, getDeviceActionsForAccessGroupsNamesWithPagination, storeAccessGroupAction } from "../../services/accessGroupActionService";
import { getSiteNamesWithPagination } from "../../services/siteService";
import { getErrorMessage } from "../../services/utilities/ServiceError";
import { appendFilter } from "../../utilities/filters";
import ActivityFrame from "../activity-frame/activity-frame";
import { useDialog } from "../dialog/DialogContext";
import TableTab from "../tabs/table-tab";
import { toast } from "react-toastify";
import { Button } from '../button/Button';
import AssignModalTwo from "../assignWizard/AssignModal";
import { PaginationModel } from "@/models/Pagination";

interface Props {
    tabs: TabSummary[];
    setTabs: (tabs: TabSummary[]) => void;
    itemId: string;
    siteId?: number;
    reloadPage: () => void
}

const AccessGroupExitActions: FC<Props> = ({ tabs, setTabs, itemId, siteId, reloadPage }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.assignedExitActions);
    const [busy, setBusy] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const dialog = useDialog();
    const [exitActions, setExitActions] = useState<AccessGroupAction[]>([]);

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    useEffect(() => {
        dataRefresh((query) => getAssignedExitActions(query));
    }, [itemId])

    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ]

    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setBusyLoader: React.Dispatch<React.SetStateAction<boolean>>) => {
        try {
            setBusyLoader(true);
            let pageNumber = 1, pagination: PaginationModel, deviceActionIds: string[] = [], rows: any[] = [];
            do {
                const exitActions = await getAssignedExitActions({pageSize: 100, goToPage: pageNumber});
                deviceActionIds = deviceActionIds.concat(exitActions?.rows.map((exitAction) => exitAction.deviceId.toString()));
                rows = rows.concat(exitActions.rows);
                pagination = exitActions.pagination;
                pageNumber++;
            } while (pageNumber <= pagination.totalPages)
            setExitActions(rows);
            setSelectedIds(deviceActionIds);
            setSelectedRows(rows)
            setOriginalSelectedIds([...deviceActionIds]); // helps us check what was mutated
            setOriginalSelectedRows(rows);
        } catch (error) {
            toast.warn(getErrorMessage(error));
        } finally {
            setBusyLoader(false);
        }
    };

    const getAssignedExitActions = (listParameters) => {
        return getAccessGroupActions(itemId, "1", listParameters);
    };

    const handleSubmit = async (toBeAdded: string[], toBeRemoved: string[]) => {
        const exitActionsIdsToBeRemoved = exitActions.filter(action => toBeRemoved.includes(action.deviceId.toString())).map(action => action.id);

        exitActionsIdsToBeRemoved.length > 0 &&
            exitActionsIdsToBeRemoved
                .filter((id) => id?.toString() !== "")
                .map(async (id) => {
                    setBusy(true);
                    await deleteAccessGroupAction(id.toString())
                        .catch((err) => {
                            dialog.pushError({
                                title: translate.byKey(TranslationKeys.error),
                                message: getErrorMessage(err),
                            });
                        })
                        .finally(() => {
                            setBusy(false);
                            reloadPage();
                        });
                });

        (await toBeAdded.length) > 0 &&
            toBeAdded.map((id) => {
                setBusy(true);
                storeAccessGroupAction({
                    agaDirection: "1",
                    accessGroupId: itemId,
                    devActionId: id,
                })
                    .then(() => {
                        toast.success(translate.byKey(TranslationKeys.exitActionsUpdated));
                    })
                    .catch((err) => {
                        dialog.pushError({
                            title: translate.byKey(TranslationKeys.error),
                            message: getErrorMessage(err),
                        });
                    })
                    .finally(() => {
                        setBusy(false);
                        reloadPage();
                    });
            });
    };

    return <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>
        <TableContextProvider
            dataMethod={getAssignedExitActions}
            tableDescriptor={columns}
            searchDescriptor={searchColumns}
            useTable
            usePagination
            useFilter
            useSearch
            useSort
            manualDataRefresh={manualDataRefresh}
            maxPageSize={DEF_PG_SIZE_SUB}
            lookup={lookup}>
            <TableTab
                title={title}
                selectRows={false}
                cyName="accessGroupExitActions" >
                <Button variant="submit" shape="rounded" className="py-1 px-5" onClick={() => setShowAddModal(true)}>{translate.byKey(TranslationKeys.manage)}</Button>
            </TableTab>
        </TableContextProvider>
        <AssignModalTwo
            isOpen={showAddModal}
            dataMethod={(listParameters?: ListQuery) => getDeviceActionsForAccessGroups({ ...listParameters, filters: appendFilter(siteId, 'Site', 'deviceImprox.site.id', listParameters?.filters ?? []) })}
            lookup={lookup}
            onClose={() => { setShowAddModal(false) }}
            assigningTitle={translate.byKey(TranslationKeys.exitActions)}
            modalTitle={translate.byKey(TranslationKeys.assignExitActions)}
            searchColumns={accessGroupDeviceActionSearch}
            tableColumns={accessGroupDeviceActionColumns}
            getAssignedItems={getAssignedItems}
            handleSubmit={handleSubmit}
            idPath={"deviceId"} />
    </ActivityFrame>
};

export default AccessGroupExitActions
